import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Theme } from '@mui/material';
import { compact, filter, join, map, size, some, takeRight, uniq } from 'es-toolkit/compat';
import { AgentStreamProgress } from 'interfaces/agentSession';

import { AgentStreamAction } from 'interfaces/agentSession';

export type ActionVisual = {
  Icon: typeof TuneRoundedIcon;
  label: string;
  color: (theme: Theme) => string;
};

export const getActionVisual = (action: AgentStreamAction): ActionVisual => {
  if (action.name.includes('chart')) {
    return {
      Icon: InsightsRoundedIcon,
      label: 'Biomarker visualization',
      color: (theme) => theme.palette.secondary.main,
    };
  }
  if (action.name.includes('table')) {
    return {
      Icon: TableChartRoundedIcon,
      label: 'Data summary',
      color: (theme) => theme.palette.warning.main,
    };
  }
  if (action.name.includes('presentation')) {
    return {
      Icon: SlideshowRoundedIcon,
      label: 'Presentation',
      color: (theme) => theme.palette.warning.dark,
    };
  }
  if (action.name.includes('session_dataset')) {
    return {
      Icon: StorageRoundedIcon,
      label: 'Analysis dataset',
      color: (theme) => theme.palette.info.main,
    };
  }
  if (action.name.includes('report_section') || action.name.includes('report_workspace')) {
    return {
      Icon: DescriptionOutlinedIcon,
      label: 'Report authoring',
      color: (theme) => theme.palette.primary.main,
    };
  }
  if (action.name.includes('session_title')) {
    return {
      Icon: TextFieldsRoundedIcon,
      label: 'Session title',
      color: (theme) => theme.palette.primary.main,
    };
  }
  if (action.name.includes('tissue_segmentation')) {
    return {
      Icon: MemoryRoundedIcon,
      label: 'Tissue segmentation',
      color: (theme) => theme.palette.warning.dark,
    };
  }
  if (action.kind === 'agent') {
    return {
      Icon: SmartToyOutlinedIcon,
      label: 'Specialist agent',
      color: (theme) => theme.palette.primary.main,
    };
  }
  if (action.kind === 'node') {
    return {
      Icon: HubOutlinedIcon,
      label: 'Analysis step',
      color: (theme) => theme.palette.secondary.main,
    };
  }
  if (action.name.includes('slide')) {
    return {
      Icon: BiotechOutlinedIcon,
      label: 'Tissue analysis',
      color: (theme) => theme.palette.info.dark,
    };
  }
  if (action.name.includes('experiment_results')) {
    return {
      Icon: BiotechOutlinedIcon,
      label: 'Biomarker results',
      color: (theme) => theme.palette.secondary.dark,
    };
  }
  if (action.name.includes('study') || action.name.includes('lab_studies')) {
    return {
      Icon: TravelExploreRoundedIcon,
      label: 'Cohort data',
      color: (theme) => theme.palette.info.dark,
    };
  }

  return {
    Icon: TuneRoundedIcon,
    label: 'Analysis',
    color: (theme) => theme.palette.primary.main,
  };
};

const INTERNAL_AGENT_TAG_REGEX = /\*\*\[[^\]]+\]\*\*\s*|\[[^\]]+_agent\]\s*/g;
const INLINE_CODE_REGEX = /`([^`]+)`/g;
const MULTI_BLANK_LINE_REGEX = /\n{3,}/g;

const EXECUTION_NOISE_PATTERNS = [
  /^Tool #\d+:/i,
  /^\d{4}-\d{2}-\d{2}.*\[(INFO|ERROR|WARNING|DEBUG)\]/,
  /^(The user wants me to|Let me\b|Now let me\b|I should\b|Excellent\b|Good\.\s+Now I'?ll\b)/i,
];

const normalizeComparisonText = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();

const sanitizeReadableLine = (value: string) =>
  value.replace(INTERNAL_AGENT_TAG_REGEX, '').replace(INLINE_CODE_REGEX, '$1').replace(/\*\*/g, '').trim();

export const sanitizeAssistantResponseContent = (content: string): string => {
  const sanitizedLines = map(content.replace(/\r\n/g, '\n').split('\n'), (line) => sanitizeReadableLine(line));
  const filteredLines = filter(sanitizedLines, (line) => {
    if (!line) {
      return true;
    }

    return !some(EXECUTION_NOISE_PATTERNS, (pattern) => pattern.test(line));
  });

  return join(filteredLines, '\n').replace(MULTI_BLANK_LINE_REGEX, '\n\n').trim();
};

export const buildReadablePlanMarkdown = (planText?: string | null): string | null => {
  const stepLines = getReadablePlanSteps(planText);

  if (size(stepLines) === 0) {
    return null;
  }

  return join(['### Plan', ...map(stepLines, (step, index) => `${index + 1}. ${step}`)], '\n');
};

export const getReadablePlanSteps = (planText?: string | null): string[] => {
  const sanitizedPlanText = sanitizeAssistantResponseContent(planText || '');
  if (!sanitizedPlanText) {
    return [];
  }

  return compact(
    map(sanitizedPlanText.split('\n'), (line) => {
      const trimmedLine = line.trim();
      if (!/^\d+\./.test(trimmedLine)) {
        return null;
      }

      return trimmedLine.replace(/^\d+\.\s*/, '').trim();
    })
  );
};

export const getReadableProgressSummary = ({
  agentStatus,
  agentProgress,
  agentActions = [],
}: {
  agentStatus?: string | null;
  agentProgress?: AgentStreamProgress | null;
  agentActions?: AgentStreamAction[];
}) => {
  const runningActionLabels = uniq(
    compact(
      map(
        filter(agentActions, (action) => action.status === 'running'),
        (action) => sanitizeReadableLine(action.label || action.name)
      )
    )
  );
  const completedActionLabels = uniq(
    compact(
      map(
        filter(agentActions, (action) => action.status === 'completed'),
        (action) => sanitizeReadableLine(action.label || action.name)
      )
    )
  );

  const progressStatusText = sanitizeReadableLine(agentProgress?.text || '');
  const agentStatusText = sanitizeReadableLine(agentStatus || '');
  const hasRunningActions = size(runningActionLabels) > 0;
  const hasActionHistory = size(agentActions) > 0;
  const currentStepText = progressStatusText
    ? progressStatusText
    : hasRunningActions
    ? agentStatusText || runningActionLabels[0]
    : !hasActionHistory
    ? agentStatusText || null
    : null;
  const currentStepProgress = agentProgress?.progress ? Math.round(agentProgress.progress) : null;
  const recentCompletedLabels = filter(takeRight(completedActionLabels, 2), (label) => label !== currentStepText);

  return {
    currentStepText,
    currentStepProgress,
    recentCompletedLabels,
  };
};

export const buildReadableProgressMarkdown = ({
  agentStatus,
  agentProgress,
  agentActions = [],
}: {
  agentStatus?: string | null;
  agentProgress?: AgentStreamProgress | null;
  agentActions?: AgentStreamAction[];
}): string | null => {
  const { currentStepText, currentStepProgress, recentCompletedLabels } = getReadableProgressSummary({
    agentStatus,
    agentProgress,
    agentActions,
  });

  if (!currentStepText && size(recentCompletedLabels) === 0) {
    return null;
  }

  const progressLines = ['### Current progress'];
  if (currentStepText) {
    const currentStepSuffix = currentStepProgress ? ` (${currentStepProgress}%)` : '';
    progressLines.push(`- In progress: ${currentStepText}${currentStepSuffix}`);
  }

  map(recentCompletedLabels, (label) => {
    progressLines.push(`- Completed: ${label}`);
  });

  return join(progressLines, '\n');
};

export const getRenderableAssistantContent = ({
  content,
  isStreaming = false,
  isLatestAssistantMessage = false,
  agentStatus,
  agentProgress,
  agentActions = [],
}: {
  content: string;
  isStreaming?: boolean;
  isLatestAssistantMessage?: boolean;
  agentStatus?: string | null;
  agentProgress?: AgentStreamProgress | null;
  agentActions?: AgentStreamAction[];
}) => {
  const sanitizedContent = sanitizeAssistantResponseContent(content);
  const hasLiveActionTimeline = size(agentActions) > 0;

  if (!isStreaming || !isLatestAssistantMessage) {
    return {
      content: sanitizedContent,
      showStreamingIndicator: isStreaming && !sanitizedContent,
    };
  }

  const readableProgressMarkdown = hasLiveActionTimeline
    ? null
    : buildReadableProgressMarkdown({ agentStatus, agentProgress, agentActions });
  const shouldAppendInlineProgress =
    Boolean(readableProgressMarkdown) &&
    !normalizeComparisonText(sanitizedContent).includes(
      normalizeComparisonText(readableProgressMarkdown || '').replace(/^### current progress\s*/i, '')
    );

  const nextContent = compact([sanitizedContent, shouldAppendInlineProgress ? readableProgressMarkdown : null]).join(
    '\n\n'
  );

  return {
    content: nextContent,
    showStreamingIndicator: !sanitizedContent,
  };
};
